import {RiskType} from '../../schema-types/globalTypes';

type RiskField = {
  title: string;
  Prompts: string[];
  Description: string;
  'Impact on Risk Rating'?: string[];
  // Bullet content for the section's help modal (introduced by ON-9175 §1.3,
  // "Helper Text Pop-ups"). Optional because the existing seven categories
  // pre-date the modal feature and don't define helper content.
  HelperText?: string[];
};

export const riskTypeToDisplays: Record<RiskType, RiskField> = {
  [RiskType.RISK_TOLERANCE]: {
    title: 'Risk Tolerance',
    Prompts: [
      'How easy did the client find the questionnaire?',
      "What is the client's understanding of what risk tolerance is?",
      'Did the client agree with the results? If not, why?',
      'Were there any inconsistencies in the risk tolerance questionnaire responses?',
    ],
    Description:
      "Please provide the client's understanding of the questionnaire, the risk tolerance score, what risk tolerance is providing. Use the client's words too.",
    'Impact on Risk Rating': [
      'Decrease',
      'Small decrease',
      'No impact',
      'Small increase',
      'Increase',
    ],
  },
  [RiskType.RISK_CAPACITY]: {
    title: 'Risk Capacity',
    Prompts: [
      'Cash Flow Analysis: Can the client meet essential expenses if investments fall?',
      'Time Horizon: Longer horizons increase capacity due to recovery time.',
      'Asset Base: More diversified and liquid assets = higher capacity.',
      'Liabilities: High debt or fixed obligations reduce capacity.',
      'Contingency Planning: Emergency funds, insurance, and income sources.',
      'Human Capital - their ability to generate income through work',
    ],
    Description:
      "Risk Capacity is a client's financial ability to withstand potential investment losses without it materially affecting their standard of living or financial goals. It is objective and quantifiable.",
    'Impact on Risk Rating': [
      'Decrease',
      'Small decrease',
      'No impact',
      'Small increase',
      'Increase',
    ],
  },
  [RiskType.TIMEFRAMES]: {
    title: 'Timeframes',
    Prompts: [
      'Record any pertinent factors in the timeframes involved for invested monies.',
      'Are there any restrictions on access?',
      'Are there any anticipated liquidity requirements or potential disruptions to assumed timelines?',
    ],
    Description:
      'Quantify the timeframes involved for these recommendations, especially where different timeframes may apply.',
    'Impact on Risk Rating': [
      'Decrease',
      'Small decrease',
      'No impact',
      'Small increase',
      'Increase',
    ],
  },
  [RiskType.CAPACITY_FOR_LOSS]: {
    title: 'Capacity for Loss',
    Prompts: [
      "What tolerance do the client's have for actual financial loss?",
      'Would there be any short-term impact on essential needs?',
      'How secure is the income (eg DB, State Pension) and does it meet their Minimum Income requirements?',
      "Example: A retiree relying solely on their pension drawdown to meet essential living costs has low or no capacity for loss - even if they say they're comfortable with risk.",
    ],
    Description:
      'Capacity for Loss refers to the degree to which a client could accept a fall in the value of their investment without it materially impacting their standard of living. It is a regulatory threshold—if a client has no capacity for loss, then any investment involving capital risk may be unsuitable.',
    'Impact on Risk Rating': [
      'Decrease',
      'Small decrease',
      'No impact',
      'Small increase',
      'Increase',
    ],
  },
  [RiskType.BEHAVIOURAL_CAPACITY]: {
    title: 'Behavioural Capacity',
    Prompts: [
      'How has the client behaved during past market downturns? What is their composure like?',
      "What is the size of the client's emergency fund relative to their expenditure? Do they feel more secure with higher cash holdings?",
      'How frequently and in what manner do they engage with their investments?',
    ],
    Description:
      "The client's emotional ability to absorb falls in the value of their investment, how have they interacted with you and their investments?",
    'Impact on Risk Rating': [
      'Decrease',
      'Small decrease',
      'No impact',
      'Small increase',
      'Increase',
    ],
  },
  [RiskType.KNOWLEDGE_AND_EXPERIENCE]: {
    title: 'Knowledge & Experience',
    Prompts: [
      "What is the client's level of investment knowledge, and how was it acquired?",
      'How long has the client been investing, and how actively have they participated in decision-making?',
      'Is there any investing “scar tissue” to be aware of?',
    ],
    Description:
      "How would you assess the client's knowledge and ability to make informed investment decisions + their relevant experience?",
    'Impact on Risk Rating': [
      'Decrease',
      'Small decrease',
      'No impact',
      'Small increase',
      'Increase',
    ],
  },
  [RiskType.VALUES]: {
    title: 'Values',
    Prompts: [
      'How do they want their money aligned with their values?',
      "Please use the client's own words.",
      'None, Sustainability Focus, Sustainability Improvers, Sustainability Impact, Sustainability Mixed Goals',
    ],
    Description:
      'Do the clients have ethical or religious needs which need to be considered when making a recommendation?',
  },
  // The three consolidated categories below are introduced by ON-9175 and
  // populated by the ON-9176 data migration. Title is taken from §1.1,
  // Prompts from §1.2, HelperText from §1.3 (verbatim — the spec requires
  // "Helper content must match exactly as defined"). Description is interim
  // prose summarising each section's purpose; ON-9177 may refine. `Impact
  // on Risk Rating` is intentionally omitted per §1.5.
  [RiskType.KNOWLEDGE_AND_BEHAVIOUR]: {
    title: 'Knowledge & Behaviour',
    Prompts: [
      'Knowledge – what they understand',
      'Experience – what they’ve been through',
      'Risk tolerance – what they say they’re comfortable with',
      'Behavioural capacity – previous behaviour and/or likely future behaviour',
    ],
    Description:
      "Capture the client's investment knowledge and experience, their stated risk tolerance, and their observed behavioural capacity.",
    HelperText: [
      "What is the client's level of investment knowledge, and how was it acquired?",
      'How long has the client been investing, and how actively have they participated in decision-making?',
      'Is there any investing “scar tissue” to be aware of?',
      'How has the client behaved during past market downturns? What is their composure like?',
      'How frequently and in what manner do they engage with their investments?',
    ],
  },
  [RiskType.VALUES_AND_SERVICE]: {
    title: 'Values & Service',
    Prompts: [
      'Capacity – how do they want their money aligned with their values?',
      'Services required – what experience of advised and/or DFM investment services do they have and document what services they desire or need, and why?',
    ],
    Description:
      "Capture the client's personal values around money in their own words, including any ESG considerations and service preferences.",
    HelperText: [
      "Please use the client's own words where possible",
      'Detail depth of feeling if ESG considerations are desired. E.g. Sustainability Focus, Sustainability Improvers, Sustainability Impact, Sustainability Mixed Goals',
    ],
  },
  [RiskType.FINANCIAL_CAPACITY]: {
    title: 'Financial Capacity',
    Prompts: [
      'Risk capacity – detail their financial ability, desire and need to take risk',
      'Capacity for loss – how much loss they could sustain without harming goals?',
      'Timeframe – for investments and how long there is to recover from losses',
    ],
    Description:
      "Capture the client's financial capacity to take and sustain investment risk, alongside the relevant timeframes and access needs.",
    HelperText: [
      'What is their driver for investing, need or desire, please detail?',
      'Can the client meet essential expenses if investments fall?',
      'Contingency Planning: Emergency funds, insurance, and income sources – higher cash positions and emergency fund relative to outgoings increase capacity for loss for their investments',
      'Human Capital – their ability to generate income through work',
      'What tolerance do the clients have for actual financial loss? Would there be any short-term impact on essential needs?',
      'How secure is the income (e.g. DB, State Pension) and does it meet their Minimum Income requirements?',
      "Example: A retiree relying solely on their pension drawdown to meet essential living costs has low or no capacity for loss - even if they say they're comfortable with risk",
      'Detail liabilities and how these will be paid off. Remember high debt or fixed obligations reduce capacity',
      'Record any pertinent factors in the timeframes involved for invested monies',
      'Detail clear and known access needs and when',
      'Are there any potential liquidity requirements or disruptions to assumed timelines?',
    ],
  },
};
